/* =========================================================================
   Sunboost custom — sb-influencer-carousel
   Rebuilt 2026-04-15.

   Playback rules:
   - Click a tile: play (with sound) from current position.
   - Click the same tile while playing: pause; position is preserved.
   - Click it again: resumes from the paused position.
   - Click a different tile: the previous video pauses (position preserved);
     the new video starts playing.
   No play-button overlay. The poster image hides while a video is playing
   and reappears on pause.
   ========================================================================= */

(function () {
  if (customElements.get('sb-influencer-carousel')) return;

  class SbInfluencerCarousel extends HTMLElement {
    constructor() {
      super();
      this.currentVideo = null;
      this.currentIndex = 0;
      this._onScroll = this._onScroll.bind(this);
      this._onResize = this._onResize.bind(this);
    }

    connectedCallback() {
      this.track = this.querySelector('.sb-ifc__track');
      this.cards = Array.from(this.querySelectorAll('.sb-ifc__card'));
      this.dotsContainer = this.querySelector('.sb-ifc__dots');
      this.prevBtn = this.querySelector('.sb-ifc__arrow--prev');
      this.nextBtn = this.querySelector('.sb-ifc__arrow--next');

      if (!this.track || this.cards.length === 0) return;

      this.desktopColumns = parseInt(this.dataset.desktopColumns, 10) || 3;
      this.mobileBreakpoint = parseInt(this.dataset.mobileBreakpoint, 10) || 749;
      this.isMobile = window.matchMedia(`(max-width: ${this.mobileBreakpoint}px)`).matches;

      this.initPlayback();
      this.initNavigation();
      requestAnimationFrame(() => this.updateState());

      this.track.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('resize', this._onResize, { passive: true });
    }

    disconnectedCallback() {
      if (this.track) this.track.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('resize', this._onResize);
      clearTimeout(this.scrollTimeout);
      clearTimeout(this.resizeTimeout);
    }

    _onScroll() {
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = setTimeout(() => this.updateState(), 50);
    }

    _onResize() {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        const wasMobile = this.isMobile;
        this.isMobile = window.matchMedia(`(max-width: ${this.mobileBreakpoint}px)`).matches;
        if (wasMobile !== this.isMobile) {
          this.initNavigation();
          this.updateState();
        }
      }, 150);
    }

    initPlayback() {
      this.cards.forEach((card) => {
        const video = card.querySelector('.sb-ifc__video');
        if (!video) return;

        card.addEventListener('click', () => {
          if (video === this.currentVideo && !video.paused) {
            video.pause();
          } else {
            this.playVideo(video, card);
          }
        });

        video.addEventListener('play', () => card.classList.add('is-playing'));
        video.addEventListener('pause', () => card.classList.remove('is-playing'));
        video.addEventListener('ended', () => {
          video.currentTime = 0;
          card.classList.remove('is-playing');
          if (this.currentVideo === video) this.currentVideo = null;
        });
      });
    }

    playVideo(video, card) {
      if (this.currentVideo && this.currentVideo !== video) {
        this.currentVideo.pause();
      }

      video.muted = false;
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {
          video.muted = true;
          const fallback = video.play();
          if (fallback && typeof fallback.catch === 'function') fallback.catch(() => {});
        });
      }
      this.currentVideo = video;
    }

    initNavigation() {
      if (!this.dotsContainer) return;
      this.dotsContainer.innerHTML = '';

      const itemsPerPage = this.isMobile ? 1 : this.desktopColumns;
      const totalPages = Math.ceil(this.cards.length / itemsPerPage);

      if (totalPages > 1) {
        const dotCount = Math.min(totalPages, 4);
        for (let i = 0; i < dotCount; i++) {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'sb-ifc__dot';
          dot.setAttribute('role', 'tab');
          dot.setAttribute('aria-label', `Go to ${this.isMobile ? 'video' : 'page'} ${i + 1}`);
          dot.addEventListener('click', () => this.goToPage(i));
          this.dotsContainer.appendChild(dot);
        }
      }

      if (this.prevBtn) this.prevBtn.onclick = () => this.navigate(-1);
      if (this.nextBtn) this.nextBtn.onclick = () => this.navigate(1);
    }

    navigate(direction) {
      const itemsPerPage = this.isMobile ? 1 : this.desktopColumns;
      const totalPages = Math.ceil(this.cards.length / itemsPerPage);
      const newPage = Math.max(0, Math.min(totalPages - 1, this.currentIndex + direction));
      this.goToPage(newPage);
    }

    goToPage(pageIndex) {
      const itemsPerPage = this.isMobile ? 1 : this.desktopColumns;
      const targetIndex = pageIndex * itemsPerPage;
      const card = this.cards[targetIndex];
      if (!card) return;

      const trackRect = this.track.getBoundingClientRect();
      const cardRect = card.getBoundingClientRect();

      let scrollPos;
      if (this.isMobile) {
        scrollPos = card.offsetLeft - (trackRect.width - cardRect.width) / 2;
      } else {
        scrollPos = card.offsetLeft;
      }

      this.track.scrollTo({ left: scrollPos, behavior: 'smooth' });
    }

    updateState() {
      if (!this.track) return;
      const trackRect = this.track.getBoundingClientRect();
      const scrollLeft = this.track.scrollLeft;
      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      const itemsPerPage = this.isMobile ? 1 : this.desktopColumns;

      if (this.isMobile) {
        const trackCenter = trackRect.left + trackRect.width / 2;
        let closestIndex = 0;
        let closestDist = Infinity;
        this.cards.forEach((card, i) => {
          const r = card.getBoundingClientRect();
          const c = r.left + r.width / 2;
          const d = Math.abs(c - trackCenter);
          if (d < closestDist) {
            closestDist = d;
            closestIndex = i;
          }
        });
        this.currentIndex = closestIndex;
      } else if (this.cards[0]) {
        const cardWidth = this.cards[0].offsetWidth;
        const gap = parseInt(getComputedStyle(this.track).gap, 10) || 0;
        const pageWidth = itemsPerPage * cardWidth + (itemsPerPage - 1) * gap;
        if (pageWidth > 0) this.currentIndex = Math.round(scrollLeft / pageWidth);
      }

      const dots = Array.from(this.dotsContainer ? this.dotsContainer.querySelectorAll('.sb-ifc__dot') : []);
      if (dots.length) {
        const activeDotIndex = Math.min(this.currentIndex, dots.length - 1);
        dots.forEach((dot, i) => {
          dot.classList.toggle('is-active', i === activeDotIndex);
          dot.setAttribute('aria-selected', i === activeDotIndex ? 'true' : 'false');
        });
      }

      if (this.prevBtn) this.prevBtn.classList.toggle('is-visible', scrollLeft > 10);
      if (this.nextBtn) this.nextBtn.classList.toggle('is-visible', scrollLeft < maxScroll - 10);
    }
  }

  customElements.define('sb-influencer-carousel', SbInfluencerCarousel);
})();
